import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import { includes, reduce } from "lodash";
import { DateTime } from "luxon";
import styled from "styled-components";
import * as Yup from "yup";
import {
  AccountWalletTransactionsQueryParams,
  useGetAccountWalletTransactionsQuery,
} from "api/baseAPI/accountWalletTransactions";
import { useCreateReportMutation } from "api/baseAPI/reports";
import {
  useGetUsersMeRetrieverPermissionsQuery,
  useGetUsersSelfQuery,
  useUpdateUserMutation,
} from "api/baseAPI/user";
import { useLazyGetWalletQuery } from "api/baseAPI/wallets";
import { Layout } from "components/layouts/Layout";
import { LinearProgress } from "components/LinearProgress";
import { FilterBar } from "components/table/FilterBar";
import { StyledCell, StyledRow, Table } from "components/table/Table";
import { Box } from "UI/Box";
import { Button } from "UI/Button";
import { Grid } from "UI/Grid";
import { Typography } from "UI/Typography";
import { ModalNotificationEmail } from "pages/reports/parts/ModalNotificationEmail";
import { getTimezone } from "utils/getTimezone";
import { ReactComponent as Download } from "utils/img/download.svg";
import { useUrlQuery } from "utils/url";
import { getCurrencyIcon } from "utils/useStyle";
import { useDateFormatter } from "hooks/useDateFormatter";
import { useNumberFormatter } from "hooks/useNumberFormatter";
import {
  useSelectedPointManager,
  useSelectedWalletManager,
} from "hooks/useSelectedEntity";
import { AccountWalletTransactionsFiltersRow } from "./parts/AccountWalletTransactionsFiltersRow";
import { ModalInfo } from "./parts/ModalInfo";

export const AccountWalletTransactions = () => {
  const { t } = useTranslation();

  const breadCrumbs = [
    [t("Отчетность"), ""],
    [t("История баланса"), ""],
  ];

  const tableCellTitles = [
    t("ID"),
    t("Дата"),
    t("Зачислено"),
    t("Списано"),
    t("Баланс"),
    t("Комментарий"),
  ];

  const [openModal, setOpenModal] = useState(false);
  const [showNotificationEmailModal, setShowNotificationEmailModal] =
    useState(false);

  const selectedPointManager = useSelectedPointManager();
  const selectedWallet = useSelectedWalletManager();
  const timezone = getTimezone();
  const { dateFormat } = useDateFormatter();
  const { amountFormat } = useNumberFormatter();

  const [getWallet, getWalletMeta] = useLazyGetWalletQuery();
  const [createReport, createReportMeta] = useCreateReportMutation();
  const getRetrieverPermissionsQuery = useGetUsersMeRetrieverPermissionsQuery();
  const getUsersSelfQuery = useGetUsersSelfQuery();
  const [updateUser] = useUpdateUserMutation();

  const createReportForm = useFormik({
    initialValues: { notification_email: "", export_type: 1 },
    onSubmit: (values, { setSubmitting }) => {
      const filters = {
        ...queryParams,
        ...(queryParams.amount__gt === "1"
          ? {
              amount__gt: "",
              amount__lt: "0",
            }
          : {}),
        transaction__service_type:
          queryParams.transaction__service_type === "internal" ? "0" : "",
        transaction__service_type__ne:
          queryParams.transaction__service_type === "client" ? "0" : "",
        model: "AccountWalletTransaction",
      };
      const reduceFilters = reduce(
        filters,
        (acc, value, key) => ({
          ...acc,
          ...(value && key !== "page" && key !== "page_size"
            ? {
                [key]: value,
              }
            : {}),
        }),
        {}
      );

      if (!values.notification_email) {
        setSubmitting(false);
        setShowNotificationEmailModal(true);
        return;
      }

      if (showNotificationEmailModal) {
        const formData = new FormData();
        formData.append("notification_email", values.notification_email);
        updateUser({ formData, id: getUsersSelfQuery?.data?.id || 0 })
          .unwrap()
          .finally(() => {
            setShowNotificationEmailModal(false);
          });
      }

      createReport({
        account_id: getWalletMeta?.data?.account?.id || 0,
        export_type: values.export_type,
        notification_email: values.notification_email,
        notification_domain: window.location.origin,
        filters: reduceFilters,
      });
    },
    validationSchema: Yup.object().shape({
      notification_email: Yup.string().when([], {
        is: () => showNotificationEmailModal === true,
        then: (schema) =>
          schema
            .required(t("Введите корректный email"))
            .email(t("Введите корректный email")),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
  });

  useEffect(() => {
    if (getUsersSelfQuery?.data?.notification_email?.length) {
      createReportForm.setFieldValue(
        "notification_email",
        getUsersSelfQuery.data?.notification_email || ""
      );
    }
  }, [getUsersSelfQuery.data?.notification_email]);

  useEffect(() => {
    if (createReportMeta.data?.uuid) {
      setOpenModal(true);
    }
  }, [createReportMeta.data]);

  useEffect(() => {
    if (selectedWallet?.id) {
      getWallet({ id: selectedWallet?.id });
    }
  }, [selectedWallet]);

  const initParameters = {
    page: "1",
    page_size: "10",
    search: "",
    account_wallet: selectedWallet?.id ? String(selectedWallet?.id) : "",
    amount__gt: "",
    amount__lt: "",
    created_at__gte: DateTime.local()
      .setZone(timezone)
      .minus({ day: 7 })
      .startOf("day")
      .toISO(),
    created_at__lte: DateTime.local().setZone(timezone).toISO(),
    transaction__service_type: "",
    transaction__service_type__ne: "",
  };

  const { queryParams, querySetters } =
    useUrlQuery<AccountWalletTransactionsQueryParams>(initParameters);

  const { page, page_size, search } = queryParams;

  const { set_page, set_page_size, set_search } = querySetters;

  const { data, isFetching, refetch } = useGetAccountWalletTransactionsQuery({
    ...queryParams,
    ...(queryParams.amount__gt === "1"
      ? {
          amount__gt: "",
          amount__lt: "0",
        }
      : {}),
    transaction__service_type:
      queryParams.transaction__service_type === "internal" ? "0" : "",
    transaction__service_type__ne:
      queryParams.transaction__service_type === "client" ? "0" : "",
  });

  const activeWallet = useMemo(
    () =>
      selectedPointManager.available_wallets.find(
        (wallet) => wallet.id === Number(queryParams.account_wallet)
      ) || selectedWallet,
    [selectedPointManager, queryParams.account_wallet]
  );

  const handleSetPage = (pageVal: number) => {
    set_page(String(pageVal));
  };

  const handleSetRowsPerPage = (rows: number) => {
    set_page("1");
    set_page_size(String(rows));
  };

  const handleSetSearch = (searchVal: string) => {
    set_page("1");
    set_search(searchVal);
  };

  const handleCreateReport = (type: number) => {
    createReportForm.setFieldValue("export_type", type);
    setTimeout(() => createReportForm.handleSubmit());
  };

  return (
    <Layout title={t("История баланса")} breadCrumbs={breadCrumbs}>
      <Box py={32} px={24}>
        {createReportMeta.isLoading ? <LinearProgress /> : <></>}
        <Table
          tableCellTitles={tableCellTitles}
          isLoading={isFetching}
          filterBar={
            <FilterBar
              search={search}
              setSearch={handleSetSearch}
              refetch={refetch}
              {...(includes(getRetrieverPermissionsQuery.data, "add_report")
                ? {
                    customButton: (
                      <Grid
                        item
                        flex
                        sm={12}
                        xl={4}
                        responsive={{
                          xl: { ml: 16, mt: 0 },
                          sm: { ml: 0, mt: 16 },
                        }}
                      >
                        <Box style={{ width: "calc(50% - 8px)" }}>
                          <Button
                            variant="outlined"
                            fullwidth
                            onClick={() => handleCreateReport(2)}
                            disabled={createReportMeta.isLoading}
                            iconStart={<Download />}
                          >
                            CSV
                          </Button>
                        </Box>
                        <Box style={{ width: "calc(50% - 8px)" }} ml={16}>
                          <Button
                            disabled={createReportMeta.isLoading}
                            onClick={() => handleCreateReport(1)}
                            fullwidth
                            variant="outlined"
                            iconStart={<Download />}
                          >
                            XLS
                          </Button>
                        </Box>
                      </Grid>
                    ),
                  }
                : {})}
            />
          }
          filtersRow={
            <AccountWalletTransactionsFiltersRow
              queryParams={queryParams}
              querySetters={querySetters}
            />
          }
          count={data ? data.count : 0}
          page={Number(page)}
          rowsPerPage={Number(page_size)}
          setPage={handleSetPage}
          setRowsPerPage={handleSetRowsPerPage}
        >
          {data &&
            data.results.map((row, i) => (
              <StyledRow key={i}>
                <StyledCell>
                  <Box
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    {row.transaction.id}
                    {row.transaction.service_type === 0 && (
                      <InternalChip>{t("Внутренняя")}</InternalChip>
                    )}
                  </Box>
                </StyledCell>
                <StyledCell>{dateFormat(row.created_at)}</StyledCell>
                <StyledCell>
                  <Typography
                    variant="subtitle2"
                    style={{ display: "flex", justifyContent: "center" }}
                  >
                    {row.amount > 0 ? (
                      <>
                        {getCurrencyIcon(activeWallet.currency || "")}
                        {amountFormat(row.amount, 4)}
                      </>
                    ) : (
                      ""
                    )}
                  </Typography>
                </StyledCell>
                <StyledCell>
                  <Typography
                    variant="subtitle2"
                    style={{ display: "flex", justifyContent: "center" }}
                  >
                    {row.amount < 0 ? (
                      <>
                        -{getCurrencyIcon(activeWallet.currency || "", "0")}
                        {amountFormat(row.amount, 4).slice(1)}
                      </>
                    ) : (
                      ""
                    )}
                  </Typography>
                </StyledCell>
                <StyledCell>
                  <Typography
                    variant="subtitle2"
                    style={{ display: "flex", justifyContent: "center" }}
                  >
                    {row.balance_after ? (
                      row.balance_after < 0 ? (
                        <>
                          -{getCurrencyIcon(activeWallet.currency || "", "0")}
                          {amountFormat(row.balance_after, 4).slice(1)}
                        </>
                      ) : (
                        <>
                          {getCurrencyIcon(activeWallet.currency || "")}
                          {amountFormat(row.balance_after, 4)}
                        </>
                      )
                    ) : (
                      ""
                    )}
                  </Typography>
                </StyledCell>
                <StyledCell>{row.comment}</StyledCell>
              </StyledRow>
            ))}
        </Table>
        {openModal ? <ModalInfo onClose={() => setOpenModal(false)} /> : <></>}
        {showNotificationEmailModal && (
          <ModalNotificationEmail
            onClose={() => {
              setShowNotificationEmailModal(false);
              createReportForm.setFieldValue(
                "notification_email",
                getUsersSelfQuery.data?.notification_email || ""
              );
            }}
            form={createReportForm}
          />
        )}
      </Box>
    </Layout>
  );
};

const InternalChip = styled.span`
  padding: 4px 8px;
  background-color: #f0f0f0;
  color: #666;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
`;
